import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS, TIER_NAMES, TIER_COLORS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { fetchLibrary } from '../services/api';
import { BookOpen, ArrowLeft, Clock, Lock, Globe, FileText, ArrowRight, Search } from 'lucide-react';

const CATEGORY_LABELS = {
  sleep: 'Sleep', nutrition: 'Nutrition', exercise: 'Exercise',
  social: 'Social', mental_wellness: 'Mental Wellness', supplements: 'Supplements', general: 'General',
};
const CATEGORY_COLORS = {
  sleep: '#5C6BC0', nutrition: '#66BB6A', exercise: '#EF5350',
  social: '#42A5F5', mental_wellness: '#FF9800', supplements: '#8D6E63', general: '#6B8E23',
};

export default function LibraryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetchLibrary().then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const tier = data?.tier || user?.membership_tier || 0;
  const articles = data?.articles || [];
  const categories = ['all', ...new Set(articles.map(a => a.category))];

  const filtered = articles
    .filter(a => a.title)
    .filter(a => filter === 'all' || a.category === filter)
    .filter(a => !search || a.title?.toLowerCase().includes(search.toLowerCase()) || a.summary?.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: COLORS.textSecondary }}>Loading library...</div>;

  // Not enough tier
  if (tier < 2) {
    return (
      <div style={{ padding: '40px 20px', maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-start', marginBottom: 32 }}>
          <button data-testid="back-btn" onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><ArrowLeft size={22} color={COLORS.textPrimary} /></button>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Research Library</h1>
        </div>
        <Lock size={56} color={COLORS.tierOptimizer} />
        <h2 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, marginTop: 16, fontFamily: 'Outfit, sans-serif' }}>Optimizer Membership Required</h2>
        <p style={{ fontSize: 15, color: COLORS.textSecondary, marginTop: 10, lineHeight: 1.6 }}>
          The full Research Library is available to Tier 2 (Optimizer) and above. Upgrade to access all publications, web-scraped research, and full article content.
        </p>
        <button onClick={() => navigate('/dashboard/membership')} style={{ marginTop: 20, padding: '14px 28px', borderRadius: 9999, border: 'none', backgroundColor: COLORS.tierOptimizer, color: '#FFF', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>View Membership Plans</button>
      </div>
    );
  }

  // Article detail view
  if (selected) {
    const catColor = CATEGORY_COLORS[selected.category] || COLORS.primary;
    return (
      <div data-testid="library-article-detail" style={{ padding: '24px 20px', maxWidth: 800, margin: '0 auto' }}>
        <button data-testid="back-to-library" onClick={() => setSelected(null)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 16, color: COLORS.textSecondary, fontSize: 14, fontWeight: 600 }}><ArrowLeft size={18} /> Back to Library</button>
        <div style={{ backgroundColor: COLORS.surface, borderRadius: 20, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          <div style={{ backgroundColor: catColor, padding: '24px 28px' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{ padding: '4px 12px', borderRadius: 9999, fontSize: 12, fontWeight: 700, backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFF' }}>{CATEGORY_LABELS[selected.category] || selected.category}</span>
              {selected.scraped && <span style={{ padding: '4px 12px', borderRadius: 9999, fontSize: 12, fontWeight: 700, backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFF', display: 'flex', alignItems: 'center', gap: 4 }}><Globe size={12} /> Web Scraped</span>}
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#FFF', fontFamily: 'Outfit, sans-serif', lineHeight: 1.2 }}>{selected.title}</h1>
            <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
              {selected.source && <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: 4 }}><BookOpen size={14} /> {selected.source}</span>}
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={14} /> {selected.read_time}</span>
              {selected.published && <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{selected.published}</span>}
            </div>
            {selected.source_url && <a href={selected.source_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, fontSize: 13, color: 'rgba(255,255,255,0.8)', textDecoration: 'underline' }}><Globe size={13} /> View original source</a>}
          </div>
          <div style={{ padding: '24px 28px' }}>
            {selected.summary && (
              <div style={{ backgroundColor: `${catColor}08`, borderLeft: `4px solid ${catColor}`, borderRadius: '0 10px 10px 0', padding: '14px 18px', marginBottom: 20 }}>
                <p style={{ fontSize: 15, color: COLORS.textPrimary, lineHeight: 1.6, margin: 0, fontWeight: 500 }}>{selected.summary}</p>
              </div>
            )}
            {selected.content ? (
              <div style={{ fontSize: 15, color: COLORS.textPrimary, lineHeight: 1.8, whiteSpace: 'pre-line' }}>{selected.content}</div>
            ) : tier < 3 ? (
              <div style={{ textAlign: 'center', padding: 32, backgroundColor: COLORS.surfaceSecondary, borderRadius: 14 }}>
                <Lock size={32} color={COLORS.tierElite} />
                <p style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary, marginTop: 12 }}>Full content available for Elite members</p>
                <p style={{ fontSize: 13, color: COLORS.textSecondary }}>Upgrade to Tier 3+ to read the complete article</p>
              </div>
            ) : (
              <p style={{ color: COLORS.textSecondary, textAlign: 'center', padding: 24 }}>Full content not yet available for this article.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Library list
  return (
    <div data-testid="library-page" style={{ padding: '24px 20px', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <button data-testid="back-btn" onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><ArrowLeft size={22} color={COLORS.textPrimary} /></button>
        <BookOpen size={26} color={COLORS.primary} />
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Research Library</h1>
        <span style={{ marginLeft: 'auto', padding: '4px 12px', borderRadius: 9999, fontSize: 12, fontWeight: 700, backgroundColor: `${TIER_COLORS[tier]}18`, color: TIER_COLORS[tier] }}>{TIER_NAMES[tier]} Access</span>
      </div>
      <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 16, marginLeft: 40 }}>
        {tier >= 3 ? 'You have full access to all publications, including web-scraped research.' : `Showing articles available at your tier. Upgrade for full access.`} · {filtered.length} articles
      </p>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 14 }}>
        <Search size={18} color={COLORS.textSecondary} style={{ position: 'absolute', left: 14, top: 12 }} />
        <input data-testid="library-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles..." style={{ width: '100%', padding: '10px 14px 10px 40px', borderRadius: 12, border: `1px solid ${COLORS.border}`, fontSize: 14, color: COLORS.textPrimary, backgroundColor: COLORS.surface }} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {categories.map(c => (
          <button key={c} onClick={() => setFilter(c)} style={{ padding: '6px 14px', borderRadius: 9999, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, textTransform: 'capitalize', backgroundColor: filter === c ? COLORS.primary : COLORS.surface, color: filter === c ? '#FFF' : COLORS.textSecondary }}>
            {c === 'all' ? `All (${articles.filter(a => a.title).length})` : (CATEGORY_LABELS[c] || c)}
          </button>
        ))}
      </div>

      {/* Articles */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textSecondary }}>No articles match your search.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(a => {
            const catColor = CATEGORY_COLORS[a.category] || COLORS.primary;
            return (
              <button key={a.article_id} data-testid={`library-article-${a.article_id}`} onClick={() => setSelected(a)}
                style={{ display: 'flex', alignItems: 'center', gap: 14, backgroundColor: COLORS.surface, borderRadius: 14, padding: 16, border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'transform 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateX(4px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateX(0)'}>
                <div style={{ width: 4, height: 44, borderRadius: 2, backgroundColor: catColor, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, backgroundColor: `${catColor}15`, color: catColor, textTransform: 'capitalize' }}>{CATEGORY_LABELS[a.category] || a.category}</span>
                    {a.scraped && <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, backgroundColor: `${COLORS.secondary}15`, color: COLORS.secondary, display: 'flex', alignItems: 'center', gap: 3 }}><Globe size={10} /> Scraped</span>}
                    {a.tier_required > 0 && <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, backgroundColor: `${TIER_COLORS[a.tier_required]}15`, color: TIER_COLORS[a.tier_required] }}>{TIER_NAMES[a.tier_required]}+</span>}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</div>
                  <div style={{ fontSize: 13, color: COLORS.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.summary}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                  <span style={{ fontSize: 12, color: COLORS.textSecondary }}>{a.read_time}</span>
                  <span style={{ fontSize: 11, color: COLORS.textSecondary }}>{a.source}</span>
                </div>
                <ArrowRight size={16} color={COLORS.textSecondary} style={{ flexShrink: 0 }} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
